<template>
  <div class="widget-template-selector">
    <v-row align="end" justify="space-between" class="mb-6">
      <v-col cols="12">
        <h2>{{ title }}</h2>
        <p class="text-body-2 text--secondary mb-0">
          {{ subtitle }}
        </p>
      </v-col>
    </v-row>

    <div class="content-layout">
      <!-- Settings Panel (Left) -->
      <div class="settings-panel">
        <v-row>
          <slot name="settings" :form_data="form_data" :is_valid="is_custom_settings_valid">
            <v-col cols="12">
              <div class="settings-section">
                <div class="settings-section-header">
                  <div>
                    <v-icon color="primary" size="20" class="mr-2">mdi-cog</v-icon>
                    <span class="settings-section-title">Form Settings</span>
                  </div>
                  <v-chip v-if="!is_form_name_valid" color="error" small outlined class="ml-1">
                    <v-icon small left>mdi-alert-circle</v-icon>
                    Required
                  </v-chip>
                </div>

                <v-text-field
                  data-validation-target="formNameInput"
                  v-model="form_data.internal_label"
                  label="Internal Form Name"
                  placeholder="Enter a name for this form"
                  outlined
                  dense
                  persistent-hint
                  hint="This is only used internally and is not visible to guests"
                  :rules="[rules.required]"
                  hide-details="auto"
                />
              </div>
            </v-col>
          </slot>

          <!-- Template Customization Section -->
          <v-col cols="12">
            <div class="settings-section">
              <div class="settings-section-header">
                <div>
                  <v-icon color="primary" size="20" class="mr-2">mdi-palette</v-icon>
                  <span class="settings-section-title">Template Customization</span>
                </div>
              </div>

              <div class="mb-4">
                <h4 class="subsection-label mb-3">Colors & Font</h4>
                <v-row dense>
                  <v-col cols="12">
                    <m-color-input
                      v-model="theme.colors.primary.value"
                      label="Primary Color"
                      :storage_key="`${storage_key_prefix}_color`"
                      dense
                      outlined
                      hide-details
                      class="mb-3"
                    />
                  </v-col>
                  <v-col cols="12">
                    <m-color-input
                      v-model="theme.colors.secondary.value"
                      label="Secondary Color"
                      :storage_key="`${storage_key_prefix}_secondary_color`"
                      dense
                      outlined
                      hide-details
                      class="mb-3"
                    />
                  </v-col>
                  <v-col cols="12">
                    <font-selector
                      v-model="theme.font_family"
                      :organization-id="organization_id"
                      label="Font Family"
                      outlined
                      dense
                      hide-details
                    />
                  </v-col>
                </v-row>
              </div>

              <div>
                <h4 class="subsection-label mt-8 mb-0">Images</h4>
                <m-image-selector
                  v-model="background_image_url"
                  :organization_id="organization_id"
                  label="Background Image"
                  hint="Recommended: JPG with aspect ratio of 4:3 (e.g. 1600px width x 1200px height)"
                  class="mb-3"
                />
                <m-image-selector
                  v-model="side_image_url"
                  :organization_id="organization_id"
                  label="Side Image"
                  hint="Recommended: JPG with aspect ratio of 7:12 (e.g. 700px width x 1200px height)"
                  class="mb-3"
                />
                <v-switch v-model="use_logo" label="Use Logo" color="primary" class="mt-0 mb-2" />
                <m-image-selector
                  v-if="use_logo"
                  v-model="logo_url"
                  :organization_id="organization_id"
                  label="Logo Image (Optional)"
                />
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Templates Panel (Right) -->
      <div class="templates-panel shaded-light px-4 py-3">
        <div class="templates-sticky-wrapper">
          <div class="templates-header">
            <div>
              <div class="d-flex justify-space-between align-center gap-2 mb-1">
                <div class="d-flex align-center mt-2 gap-2" data-validation-target="templateGallery">
                  <v-icon color="primary" size="20">mdi-card-multiple-outline</v-icon>
                  <h3 class="settings-section-title">Select a Template</h3>
                </div>
                <v-chip
                  v-if="!is_template_valid"
                  color="error"
                  class="flex-shrink-0"
                  small
                  outlined
                >
                  <v-icon small left>mdi-alert-circle</v-icon>
                  Required
                </v-chip>
              </div>
            </div>
          </div>

          <div class="templates-scroll-container">
            <slot
              name="template-gallery"
              :selected_template="selected_template"
              :theme="theme"
              :organization_name="organization_name"
              :background_image_url="background_image_url"
              :side_image_url="side_image_url"
              :logo_url="logo_url"
              :use_logo="use_logo"
              :form_data="form_data"
              :on_template_selected="onTemplateSelected"
              :on_template_deselected="deselectTemplate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FontSelector from '../input/font-selector.vue';
import mColorInput from '../input/m-color-input.vue';
import MImageSelector from '../input/image-selector.vue';
import GuidedValidationMixin from '../../mixins/guided-validation.js';
import { estimateSecondaryColor } from '../../js/color.js';

/**
 * Reusable template selector for widget forms i.e. the step users reach after initiating the creation of a new form.
 * Provides form settings, theme customization (colors, fonts, images), and template gallery.
 *
 * @example
 * <widget-template-selector
 *   title="Create Guest Check-In Form"
 *   subtitle="Choose a template and customize..."
 *   :organization_id="org_id"
 *   :initial_form_data="{ internal_label: '', ... }"
 *   @form-created="handleFormCreated"
 *   @form-valid-changed="isValid => ..."
 * >
 *   <template #settings="{ form_data, is_valid }">
 *     <!-- Custom settings like consent options -->
 *   </template>
 *   <template #template-gallery="{ selected_template, theme, ... }">
 *     <widget-template-gallery ... />
 *   </template>
 * </widget-template-selector>
 */
export default {
  name: 'WidgetTemplateSelector',

  mixins: [GuidedValidationMixin],

  components: {
    FontSelector,
    mColorInput,
    MImageSelector,
  },

  props: {
    organization_id: {
      type: String,
      required: true,
    },

    /**
     * Primary heading
     */
    title: {
      type: String,
      default: 'Create Widget Form',
    },

    /**
     * Subtitle/description text
     */
    subtitle: {
      type: String,
      default:
        "Choose a template and customize the basic styles. You'll be able to customize further on the next page.",
    },

    /**
     * Default primary color
     */
    default_primary_color: {
      type: String,
      default: 'rgba(198, 40, 40, 1)',
    },

    /**
     * Default font family
     */
    default_font_family: {
      type: String,
      default: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },

    /**
     * Default background image URL
     */
    default_background_image: {
      type: String,
      default: '',
    },

    /**
     * Default side image URL
     */
    default_side_image: {
      type: String,
      default: '',
    },

    /**
     * Initial form data
     */
    initial_form_data: {
      type: Object,
      default: () => ({
        internal_label: '',
      }),
    },

    /**
     * Storage key prefix for remembering user preferences
     */
    storage_key_prefix: {
      type: String,
      default: 'widget_template',
    },

    /**
     * API endpoint for creating the form
     */
    create_endpoint: {
      type: String,
      required: true,
    },

    /**
     * Custom validation function for additional form data beyond the internal_label.
     * Returns true if valid, false otherwise
     */
    validate_custom_settings: {
      type: Function,
      default: () => true,
    },
    
    /**
     * Prop to indicate if we are in "re-apply" mode.
     * This is used to bypass internal_label validation.
     */
    is_re_apply_mode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const storage_prefix = this.storage_key_prefix;
    const stored_primary = window.sessionStorage.getItem(`${storage_prefix}_color`);
    const stored_font = window.sessionStorage.getItem(`${storage_prefix}_font_family`);

    return {
      creating_form: false,
      selected_template: null,
      organization_name: 'Your Organization',
      form_data: { ...this.initial_form_data },
      theme: {
        colors: {
          primary: {
            label: 'Primary Color',
            value: stored_primary || this.default_primary_color,
          },
          secondary: {
            label: 'Secondary Color',
            value: null,
          },
        },
        font_family: stored_font || this.default_font_family,
      },
      background_image_url: this.default_background_image,
      side_image_url: this.default_side_image,
      use_logo: false,
      logo_url: null,
      rules: { required: value => !!value || 'This field is required' },
      validation_warnings: [],
    };
  },

  computed: {
    computed_secondary_color() {
      if (this.theme.colors.secondary.value) {
        return this.theme.colors.secondary.value;
      }
      return estimateSecondaryColor(this.theme.colors.primary.value);
    },

    is_form_name_valid() {
      // In re-apply mode, the name field is hidden by the parent component, so validation isn't needed
      if (this.is_re_apply_mode) {
        return true;
      }
      return !!this.form_data.internal_label && this.form_data.internal_label.trim() !== '';
    },

    is_custom_settings_valid() {
      return this.validate_custom_settings(this.form_data);
    },

    is_template_valid() {
      return !!this.selected_template;
    },

    is_form_valid() {
      return this.is_form_name_valid && this.is_custom_settings_valid && this.is_template_valid;
    },

  },

  watch: {
    is_form_valid: {
      handler(newVal) {
        this.$emit('form-valid-changed', newVal);
      },
      immediate: true,
    },

    initial_form_data: {
      handler(newVal) {
        this.form_data = { ...newVal };
      },
      deep: true,
    },

    'theme.font_family'(newVal, oldVal) {
      if (!newVal) {
        window.sessionStorage.removeItem(`${this.storage_key_prefix}_font_family`);
        return;
      }

      if (newVal !== oldVal && oldVal !== undefined) {
        window.sessionStorage.setItem(`${this.storage_key_prefix}_font_family`, newVal);
      }
    },

    'theme.colors.primary.value'(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        const newSecondary = estimateSecondaryColor(newVal);
        this.theme.colors.secondary.value = newSecondary;
        
        if (this.storage_key_prefix) {
          window.sessionStorage.setItem(`${this.storage_key_prefix}_secondary_color`, newSecondary);
        }
      }
    },

    is_form_name_valid() {
      this.updateValidationWarnings();
    },
    is_custom_settings_valid() {
      this.updateValidationWarnings();
    },
    is_template_valid() {
      this.updateValidationWarnings();
    },
  },

  created() {
    const stored_secondary = window.sessionStorage.getItem(
      `${this.storage_key_prefix}_secondary_color`
    );
    if (stored_secondary) {
      this.theme.colors.secondary.value = stored_secondary;
    } else if (!this.theme.colors.secondary.value) {
      this.theme.colors.secondary.value = this.computed_secondary_color;
    }
  },

  mounted() {
    this.loadOrganizationName();
    this.updateValidationWarnings();
  },

  methods: {
    onTemplateSelected(template) {
      this.selected_template = template;
    },

    deselectTemplate() {
      this.selected_template = null;
    },

    loadOrganizationName() {
      this.$rc.getOrganizationLabel(this.organization_id, label => {
        this.organization_name = label;
      });
    },

    updateValidationWarnings() {
      this.validation_warnings = [];

      if (!this.is_form_name_valid) {
        this.validation_warnings.push(
          this.mixinCreateWarning('Internal Form Name is required', {
            target: 'formNameInput',
          })
        );
      }

      if (!this.is_custom_settings_valid) {
        this.validation_warnings.push(
          this.mixinCreateWarning('Consent settings are incomplete', {
            target: 'consentSettingsSection',
          })
        );
      }

      if (!this.is_template_valid) {
        this.validation_warnings.push(
          this.mixinCreateWarning('A template must be selected', {
            target: 'templateGallery',
          })
        );
      }

      this.$emit('validation-warnings-changed', this.validation_warnings);
    },

    fixWarning(warning) {
      this.mixinGuideToFix(warning);
    },

    getInvalidFields() {
      const invalid_fields = [];
      if (!this.is_form_name_valid) invalid_fields.push('Internal Form Name');
      if (!this.is_custom_settings_valid) invalid_fields.push('Custom Settings');
      if (!this.is_template_valid) invalid_fields.push('Template Selection');
      return invalid_fields.join(', ');
    },

    buildCreationParams() {
      return {
        organization_id: this.organization_id,
        internal_label: this.form_data.internal_label.trim(),
        template_type:
          this.selected_template && this.selected_template.id !== 'blank'
            ? this.selected_template.id
            : null,
        theme: {
          primary_color: this.theme.colors.primary.value,
          secondary_color: this.theme.colors.secondary.value || this.computed_secondary_color,
          font_family: this.theme.font_family,
        },
        images: {
          page_background_url: this.background_image_url,
          side_image_url: this.side_image_url,
          use_logo: this.use_logo,
          logo_image_url: this.use_logo ? this.logo_url : null,
        },
        template_request_type: this.is_re_apply_mode ? 'update' : 'create',
        ...this.form_data,
      };
    },

    async createForm() {
      if (!this.is_form_valid) {
        this.updateValidationWarnings();
        return;
      }

      if (this.creating_form) return;
      this.creating_form = true;

      const params = this.buildCreationParams();

      this.$rc.rest({
        spinner: true,
        endpoint: this.create_endpoint,
        params,
        on_success: data => {
          this.$emit('form-created', data);
          this.creating_form = false;
        },
        on_error: data => {
          this.$rc.alert('Unable to create form', data.message);
          this.creating_form = false;
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.widget-template-selector {
  width: 100%;
}

.content-layout {
  display: flex;
  gap: 2rem;

  @media (max-width: 1263px) {
    flex-direction: column;
  }
}

.settings-panel {
  flex: 0 0 auto;
  width: 100%;

  @media (min-width: 1264px) {
    width: 550px;
    max-width: 550px;
  }
}

.templates-panel {
  flex: 1;
  min-width: 0;

  @media (max-width: 1263px) {
    width: 100%;
  }
}

.templates-sticky-wrapper {
  @media (min-width: 1264px) {
    position: sticky;
    top: 1rem;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 2rem);
  }
}

.templates-header {
  flex-shrink: 0;
  margin-bottom: 1rem;
}

.templates-scroll-container {
  @media (min-width: 1264px) {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 0.5rem;
    margin-right: -0.5rem;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.05);
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
      border-radius: 4px;

      &:hover {
        background: rgba(0, 0, 0, 0.3);
      }
    }
  }
}

.settings-section {
  background: var(--color-bg-shaded-light);
  border-radius: var(--border-radius-default);
  padding: 1.25rem;
  height: 100%;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #c8ccd0;
  }
}

.settings-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 0.5rem;
}

.settings-section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}

.subsection-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
}

:deep(.v-input__slot) {
  margin-bottom: 0;
}

:deep(.v-messages) {
  min-height: 14px;
}
</style>
